import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Brightness4,
  Brightness7,
  AccountCircle,
  SettingsBrightness,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ChangePasswordDialog from './ChangePasswordDialog';

const Header = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [themeMenuAnchor, setThemeMenuAnchor] = React.useState(null);
  const [logoExists, setLogoExists] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme, useSystemTheme, toggleUseSystemTheme } = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // Check if logo.png exists - only once when component mounts
    const checkLogo = async () => {
      try {
        const logoKey = 'logo_exists';
        const cached = localStorage.getItem(logoKey);
        
        if (cached !== null) {
          setLogoExists(cached === 'true');
          return;
        }

        const response = await fetch('/logo.png', { method: 'HEAD' });
        const exists = response.ok;
        setLogoExists(exists);
        localStorage.setItem(logoKey, exists.toString());
      } catch {
        setLogoExists(false);
        localStorage.setItem('logo_exists', 'false');
      }
    };
    
    checkLogo();
  }, []); // Empty dependency array - only run once

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    handleClose();
    setChangePasswordOpen(true);
  };

  // Format date according to current language
  const formatDate = () => {
    return new Date().toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleThemeMenuOpen = (event) => {
    setThemeMenuAnchor(event.currentTarget);
  };

  const handleThemeMenuClose = () => {
    setThemeMenuAnchor(null);
  };

  const handleThemeToggle = () => {
    toggleTheme();
    handleThemeMenuClose();
  };

  const handleSystemThemeToggle = () => {
    toggleUseSystemTheme();
    handleThemeMenuClose();
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {logoExists ? (
            <Box
              component="img"
              src="/logo.png"
              alt={t('common.appTitle')}
              sx={{
                height: 40,
                mr: 2,
                display: 'block',
                flexGrow: 0
              }}
            />
          ) : (
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {t('common.appTitle')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, justifyContent: 'flex-end' }}>
            <Typography variant="body1">
              {formatDate()}
            </Typography>
            <LanguageSelector />
            <Tooltip title={useSystemTheme ? t('common.useSystemTheme') : (isDarkMode ? t('common.lightMode') : t('common.darkMode'))}>
              <IconButton 
                color="inherit" 
                onClick={handleThemeMenuOpen}
                aria-label={t('common.darkMode')}
              >
                {useSystemTheme ? <SettingsBrightness /> : (isDarkMode ? <Brightness7 /> : <Brightness4 />)}
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={themeMenuAnchor}
              open={Boolean(themeMenuAnchor)}
              onClose={handleThemeMenuClose}
            >
              <MenuItem onClick={handleSystemThemeToggle}>
                {useSystemTheme ? t('common.manualTheme') : t('common.useSystemTheme')}
              </MenuItem>
              {!useSystemTheme && (
                <MenuItem onClick={handleThemeToggle}>
                  {isDarkMode ? t('common.lightMode') : t('common.darkMode')}
                </MenuItem>
              )}
            </Menu>
            {user && (
              <>
                <IconButton
                  size="large"
                  onClick={handleMenu}
                  color="inherit"
                  aria-label={t('common.userMenu')}
                >
                  <AccountCircle />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                >
                  <MenuItem 
                    disabled 
                    sx={{ 
                      opacity: '1 !important',
                      fontWeight: 'bold',
                      bgcolor: (theme) => theme.palette.mode === 'light' 
                        ? 'rgba(0, 0, 0, 0.04)' 
                        : 'rgba(255, 255, 255, 0.08)'
                    }}
                  >
                    {user.username}
                  </MenuItem>
                  {user.role === 'ADMIN' && (
                    <MenuItem onClick={() => navigate(window.location.pathname.includes('/admin') ? '/dashboard' : '/admin')}>
                      {window.location.pathname.includes('/admin') ? t('common.switchToDashboard') : t('common.switchToAdmin')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleChangePassword}>
                    {t('common.changePassword')}
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    {t('common.logout')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </>
  );
};

export default Header; 